/* eslint-disable complexity */
import {ReactComponent as TokenIcon} from "../../core/ui/icon/Token.svg";
import {ReactComponent as MoreIcon} from "../../core/ui/icon/More.svg";

import "./_asset-form-element.scss";

import {convertFromBaseUnits} from "@tinymanorg/tinyman-js-sdk";
import classNames from "classnames";
import {FormField, NumberInput, NumberInputProps} from "@hipo/react-ui-toolkit";
import {Fragment, useRef} from "react";

import DollarValue from "../../component/dollar-value/DollarValue";
import {formatNumber, isFiniteNumber} from "../../core/util/number/numberUtils";
import {useAssetPrices} from "../price/context/AssetPriceContext.utils";
import {
  calculateAssetPrice,
  combineAssetUnitAndId,
  getAssetBalance
} from "../util/assetUtils";
import {MAX_AMOUNT_VALUE} from "./AssetFormElement.constants";
import {useSelectedAccount} from "../../wallet/util/context/WalletContext.utils";
import {SimpleAsset} from "../util/assetModels";
import Typography, {TypographyProps} from "../../component/typography/Typography";
import Button from "../../component/button/Button";
import AssetNameWithLogo from "../name/with-logo/AssetNameWithLogo";
import {getSelectableAssetsFromSearchParams} from "../../core/widgetUtils";

interface AssetFormElementProps {
  asset: SimpleAsset | null;
  inputProps?: Omit<NumberInputProps, "onChange"> & {
    id: string;
    name: string;
    onChange: (name: string, value: string) => void;
    value: string;
    max?: number;
  };
  typography?: TypographyProps<"button">["variant"];
  customClassName?: string;
  hasError?: boolean;
  onBrowseToSelectAssetView?: VoidFunction;
}

function AssetFormElement({
  asset,
  inputProps,
  customClassName,
  hasError,
  onBrowseToSelectAssetView,
  typography
}: AssetFormElementProps) {
  const assetBalanceElementRef = useRef<HTMLDivElement>();
  const assetUnitAndIdElementRef = useRef<HTMLDivElement>();

  const selectableAssets = getSelectableAssetsFromSearchParams();
  const isAssetChangeDisabled =
    typeof onBrowseToSelectAssetView !== "function" ||
    !selectableAssets ||
    selectableAssets.length === 0;

  const selectedAccount = useSelectedAccount();
  const priceData = useAssetPrices();
  const hasInput = inputProps !== undefined;
  const usdValue =
    priceData && hasInput && inputProps.value && asset
      ? calculateAssetPrice(priceData, asset.id, Number(inputProps.value))
      : null;
  const assetBalance = asset ? getAssetBalance(selectedAccount, Number(asset.id)) : 0;

  const hasDollarValue = isFiniteNumber(usdValue);

  return (
    <div
      className={classNames("is-flex-column", "asset-form-element", customClassName, {
        "asset-form-element--has-error": hasError
      })}>
      <div className={"asset-name-with-amount-value is-flex-column"}>
        {asset && (
          <div
            className={
              "is-vertically-centered has-space-between asset-form-element__asset-info"
            }>
            {renderAssetInfo()}

            <Typography
              ref={assetUnitAndIdElementRef}
              customClassName={classNames("asset-form-element__asset-unit-and-id", {
                "asset-form-element__asset-unit-and-id--is-hidden": hasError
              })}
              textColor={"text-gray"}>
              {combineAssetUnitAndId(asset)}
            </Typography>

            <Typography
              ref={assetBalanceElementRef}
              customClassName={classNames("asset-form-element__balance", {
                "asset-form-element__balance--is-hidden": !(hasInput && hasError)
              })}
              textColor={hasError ? "helper-danger" : "text-gray"}
              variant={"caption"}>
              {renderAssetBalance()}
            </Typography>
          </div>
        )}

        {hasInput && asset && (
          <FormField
            customClassName={classNames(
              "is-aligned-right",
              "asset-form-element__form-field",
              {
                "asset-form-element__form-field--has-value": Boolean(
                  inputProps.value && hasDollarValue
                )
              }
            )}>
            <NumberInput
              {...inputProps}
              placeholder={"0.00"}
              hasError={hasError}
              customClassName={"typography--display asset-form-element__input"}
              formatProps={{
                shouldFormatToLocaleString: true
              }}
              maximumFractionDigits={asset?.decimals}
              onChange={handleAmountChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />

            {hasDollarValue && (
              <DollarValue
                variant={"caption"}
                textColor={"text-gray"}
                usdValue={usdValue}
              />
            )}
          </FormField>
        )}
      </div>
    </div>
  );

  function renderAssetBalance() {
    const amount = formatNumber({
      minimumFractionDigits: 2,
      maximumFractionDigits: asset!.decimals
    })(convertFromBaseUnits(asset!.decimals, assetBalance));
    const amountWithUnitName = `${amount} ${asset!.unit_name}`;

    return `Balance ${amountWithUnitName}`;
  }

  function handleAmountChange(event: React.SyntheticEvent<HTMLInputElement>) {
    if (inputProps) {
      let {value} = event.currentTarget;

      // Amount must be a positive number and smaller than 2^64-1
      if (value.includes("-")) {
        value = value.replace("-", "");
      } else if (
        parseFloat(value) >
        // eslint-disable-next-line no-magic-numbers
        MAX_AMOUNT_VALUE * Math.pow(10, asset ? -asset.decimals : 0)
      ) {
        value = value.slice(0, value.length - 1);
      }

      inputProps.onChange(event.currentTarget.name, value);
    }
  }

  function handleFocus() {
    if (
      assetUnitAndIdElementRef.current &&
      assetBalanceElementRef.current &&
      !hasError &&
      selectedAccount
    ) {
      assetBalanceElementRef.current.classList.remove(
        "asset-form-element__balance--is-hidden"
      );
      assetUnitAndIdElementRef.current.classList.add(
        "asset-form-element__asset-unit-and-id--is-hidden"
      );
    }
  }

  function renderAssetInfo() {
    const AssetInfo = asset ? (
      <Fragment>
        <AssetNameWithLogo asset={asset} />
      </Fragment>
    ) : (
      <div className={"typography--body is-bold-text is-vertically-centered"}>
        <span
          className={
            "is-centered asset-form-element__select-asset-view-button__token-icon"
          }>
          <TokenIcon />
        </span>

        {"Select an asset"}
      </div>
    );

    return isAssetChangeDisabled ? (
      renderReadOnlyAssetInfo(AssetInfo)
    ) : (
      <Button
        customClassName={classNames("asset-form-element__select-asset-view-button", {
          "asset-form-element__select-asset-view-button--is-unselected": !asset
        })}
        typography={typography}
        onClick={onBrowseToSelectAssetView}>
        {AssetInfo}

        <MoreIcon />
      </Button>
    );
  }

  function renderReadOnlyAssetInfo(assetInfo: React.ReactNode) {
    return <div className={"asset-form-element__asset-info-container"}>{assetInfo}</div>;
  }

  function handleBlur() {
    if (
      assetUnitAndIdElementRef.current &&
      assetBalanceElementRef.current &&
      !hasError &&
      selectedAccount
    ) {
      assetBalanceElementRef.current.classList.add(
        "asset-form-element__balance--is-hidden"
      );
      assetUnitAndIdElementRef.current.classList.remove(
        "asset-form-element__asset-unit-and-id--is-hidden"
      );
    }
  }
}

export default AssetFormElement;
